import { defineStore } from 'pinia'
import { ref, computed } from 'vue'
import type { User, UserRole } from '@/types'
import {
  login as kcLogin,
  logout as kcLogout,
  isAuthenticated as kcIsAuthenticated,
  getUserFromToken,
  getToken,
  onTokenUpdate,
} from '@/lib/keycloak'

// Normalize legacy roles to new role model
function normalizeRole(role: UserRole): UserRole {
  switch (role) {
    case 'clinician':
      return 'provider'
    case 'clinic_admin':
      return 'provider_admin'
    case 'system_admin':
      return 'platform_admin'
    default:
      return role
  }
}

// Check if role is a provider type (can access patient data)
function isProviderRole(role: UserRole): boolean {
  const normalized = normalizeRole(role)
  return ['provider', 'provider_admin', 'platform_admin', 'coach', 'aide_soignant'].includes(normalized)
}

// Check if role is an admin type
function isAdminRole(role: UserRole): boolean {
  const normalized = normalizeRole(role)
  return ['provider_admin', 'platform_admin'].includes(normalized)
}

// Check if role is platform admin
function isPlatformAdminRole(role: UserRole): boolean {
  const normalized = normalizeRole(role)
  return normalized === 'platform_admin'
}

export const useAuthStore = defineStore('auth', () => {
  // State
  const user = ref<User | null>(null)
  const token = ref<string | null>(null)
  const isLoading = ref(true)
  const error = ref<string | null>(null)

  // Getters
  const isAuthenticated = computed(() => !!user.value && !!token.value)
  const userRole = computed(() => user.value?.role || 'patient')
  const normalizedRole = computed(() => normalizeRole(userRole.value))
  const userName = computed(() => user.value?.name || '')
  const isPatient = computed(() => normalizedRole.value === 'patient')
  const isProvider = computed(() => isProviderRole(userRole.value))
  const isAdmin = computed(() => isAdminRole(userRole.value))
  const isPlatformAdmin = computed(() => isPlatformAdminRole(userRole.value))
  // Legacy alias
  const isClinician = computed(() => isProvider.value)
  const hasRole = computed(() => (role: UserRole) =>
    user.value?.roles.includes(role) ?? false
  )

  // Actions
  async function initialize() {
    isLoading.value = true
    error.value = null

    try {
      // Keycloak is already initialized in main.ts
      // Just sync the state from it
      const authenticated = kcIsAuthenticated()

      if (authenticated) {
        user.value = getUserFromToken()
        token.value = getToken() || null

        // Listen for token updates
        onTokenUpdate((newToken) => {
          token.value = newToken
        })

        // For patients, fetch patientId mapping from backend
        if (user.value && normalizeRole(user.value.role) === 'patient') {
          try {
            const response = await fetch(`${window.CONFIG?.API_BASE_URL || ''}/clinical/me`, {
              headers: {
                'Authorization': `Bearer ${token.value}`,
                'Content-Type': 'application/json',
              },
            })
            if (response.ok) {
              const data = await response.json()
              if (data.patientId && user.value) {
                user.value = { ...user.value, patientId: data.patientId }
                console.log('[Auth] Patient ID mapped:', data.patientId)
              }
            }
          } catch (e) {
            console.warn('[Auth] Could not fetch patient profile:', e)
          }
        }
      }
    } catch (err) {
      console.error('[Auth] Initialization failed:', err)
      error.value = 'Échec de l\'authentification'
    } finally {
      isLoading.value = false
    }
  }

  async function login() {
    isLoading.value = true
    error.value = null

    try {
      await kcLogin()
    } catch (err) {
      console.error('[Auth] Login failed:', err)
      error.value = 'Échec de la connexion'
      isLoading.value = false
    }
  }

  function logout() {
    // Don't clear state here - it triggers Vue Router guard which conflicts
    // with Keycloak's redirect. Just redirect to Keycloak logout directly.
    // Keycloak will handle everything and redirect back to the app.
    // See: https://www.keycloak.org/docs/latest/securing_apps/#_javascript_adapter
    kcLogout()
  }

  function checkAuth() {
    return kcIsAuthenticated()
  }

  return {
    // State
    user,
    token,
    isLoading,
    error,
    // Getters
    isAuthenticated,
    userRole,
    normalizedRole,
    userName,
    isPatient,
    isProvider,
    isAdmin,
    isPlatformAdmin,
    isClinician, // Legacy alias
    hasRole,
    // Actions
    initialize,
    login,
    logout,
    checkAuth,
  }
})
